import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Schedule as ScheduleIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { 
  FaClock, 
  FaRoute,
  FaShieldAlt
} from 'react-icons/fa';
import { 
  MdAccessTime, 
  MdDirections 
} from 'react-icons/md';
import { 
  GiPathDistance 
} from 'react-icons/gi';
import { 
  HiOutlineClock, 
  HiOutlineArrowRight 
} from 'react-icons/hi';
import { 
  RiTimeLine, 
  RiDirectionLine 
} from 'react-icons/ri';
import { SmartCTA } from '../components/SmartCTAs';
import marketingTheme from '../themes/marketingTheme';

const LeanWasteAnalytics = () => {
  const features = [
    {
      icon: <HiOutlineClock style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Wait Time',
      description: 'Analysis of wait time in high-touch industrial workflows'
    },
    {
      icon: <GiPathDistance style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Excess Motion',
      description: 'Analysis of excess motion in high-touch industrial workflows'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            AI Vision for Lean Wastes
          </Typography>
          <Typography variant="h5" sx={{ 
            color: marketingTheme.palette.primary.main, 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for Empirical Comprehension of Lean Wastes.
          </Typography>
        </Box>

        {/* Main Content with Case Study */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              When mounting production targets collide with persistent skill shortages, you're left with an impossible task: do more with the resources you have. Traditional approaches like increasing pace or adding pressure only lead to fatigue and quality issues.
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src="/images/worker_ergonomics.png" 
                alt="AI Vision for Lean Waste Analytics" 
                style={{ 
                  maxWidth: '100%',
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>

            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              Why limit your output due to skill shortages when smarter workflows could unlock capacity you didn't know existed? Our system shows exactly where your valuable skilled workers are losing time to waiting and unnecessary movement, yielding expanded capacity and reduced fatigue.
              <br /><br />
              <strong>Waiting</strong> and <strong>Excess Motion</strong> are key Lean wastes. Our camera can quantify these wastes to improve productivity and safety. See exactly where time and energy are being lost.
              <br /><br />
              MotionLogic® is a digital augmentation of Lean. Enhancing standard Lean methods with continuous, factual, and quantifiable observations of your process. We perform visual data capture in a manner which preserves privacy.
              <br /><br />
              MotionLogic® can enhance the scope and volume of insights that Lean professionals deliver.
              <br /><br />
              Current Lean approaches are limited by the single moment-in-time capture, and the manual effort in measurement and collation of data. We transform video into structured datasets through Scene Understanding.
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src="/images/process_segmentation.png" 
                alt="AI Vision for Production Flow" 
                style={{ 
                  maxWidth: '100%',
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>

            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              Our Event Builder interface enables production personnel to customise the ergonomic events, and tool usage events, that are recognised by the camera. So that the camera can provide insights on the root causes of waiting and excess motion.
              <br /><br />
              Our Scene Understanding technology transforms video into structured datasets. We present these as comprehensible reports, which can be used to inform Lean initiatives.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              p: 3,
              textAlign: 'center',
              height: 'fit-content'
            }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 4 }}>
                Hypothetical Case Study
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                  $1B
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  COGS (of an ergonomically-intensive workflow)
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold' }}>
                  5%
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  *Reduction In Waiting and Excess Motion
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                  Yields
                </Typography>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold', mb: 2 }}>
                  $50M
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  Margin Enhancement
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic',
                display: 'block',
                fontSize: '0.7rem'
              }}>
                *Hypothetical, this scenario is provided for illustrative purposes only, the accuracy and applicability of this scenario may be influenced by unique factors inherent to each user's environment and practices.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ 
            color: 'white', 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 600
          }}>
            Purpose
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(237, 185, 46, 0.3)'
                  }
                }}>
                  <CardContent sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontWeight: 600
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6
                    }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SmartCTA
            context="experience-demo"
            fallbackHref="/contact"
            fallbackText="Learn More"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: marketingTheme.palette.primary.main,
              color: '#000',
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              '&:hover': {
                backgroundColor: marketingTheme.palette.primary.dark,
              },
            }}
          />
        </Box>

        {/* Privacy Notice */}
        <Paper sx={{ 
          backgroundColor: 'rgba(29, 209, 161, 0.1)', 
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4, 
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <FaShieldAlt style={{ fontSize: 60, color: '#1dd1a1', flexShrink: 0 }} />
          <Box>
            <Typography variant="h5" sx={{ 
              color: '#1dd1a1',
              fontWeight: 600,
              mb: 2
            }}>
              Pixel Privacy
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6
            }}>
              MotionLogic's camera technology preserves the privacy of individuals in visual scenes.
              We pseudonymise our data, and remove individually identifying features, including blurring faces.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LeanWasteAnalytics;
